import { ResponsiveHidingProps, useFullTheme } from '~/design';
import { Tag, TagProps } from '../Tag';
import { Text } from '../Text';
import { useResponsiveHideStyle } from '~/utils/styling';

export interface DividerProps extends ResponsiveHidingProps {
  /**
   * The text or tag of the divider.
   */
  children?: string | React.ReactElement<TagProps>;

  /**
   * The direction of the divider.
   */
  direction?: 'horizontal' | 'vertical';

  /**
   * The placement of the text within the divider. "left" and "right" only affect horizontal
   * dividers, "top" and "bottom" only affect vertical dividers. If not set, the text will be
   * centered within the divider.
   */
  textPlacement?: 'left' | 'right' | 'top' | 'bottom';

  /**
   * Set to `0` to remove the default margin.
   */
  m?: 0;
}

/**
 *
 */
export function Divider({
  children,
  direction = 'horizontal',
  hideAbove,
  hideBelow,
  m,
  textPlacement,
}: DividerProps) {
  const theme = useFullTheme();
  const isVertical = direction === 'vertical';
  const border = theme.borders.regular;
  const hideStyle = useResponsiveHideStyle({ hideBelow, hideAbove });

  if (typeof m !== 'undefined' && m !== 0) {
    throw new Error('margin prop must be set to 0 to remove the default margin');
  }

  if (!children) {
    return (
      <hr
        css={[
          hideStyle,
          {
            borderWidth: 0,
            borderColor: border.borderColor,
            borderStyle: border.borderStyle,
          },
          isVertical && {
            borderRightWidth: border.borderWidth,
            marginBlock: 0,
            alignSelf: 'stretch',
            height: 'unset',
          },
          !isVertical && {
            borderTopWidth: border.borderWidth,
            marginInline: 0,
            marginBlock: theme.spacing[4],
            width: '100%',
          },
          {
            margin: m,
            marginBlock: m,
          },
        ]}
      />
    );
  }

  if (typeof children !== 'string' && children.type !== Tag) {
    throw new Error('children must be either a string or an instance of the Tag component');
  }

  return (
    <div
      css={[
        hideStyle,
        {
          '&::before, &::after': {
            border: 0,
            borderColor: border.borderColor,
            borderStyle: border.borderStyle,
            content: '""',
          },
          border: 0,
          borderColor: border.borderColor,
          borderStyle: border.borderStyle,
          borderWidth: 0,
          display: 'flex',
          height: 'auto',
          textAlign: 'center',
          width: '100%',
          whiteSpace: 'nowrap',
        },
        isVertical && {
          alignSelf: 'stretch',
          flexDirection: 'column',
          '&::before, &::after': {
            borderLeftWidth: border.borderWidth,
            height: '100%',
            left: '50%',
            position: 'relative',
          },
          '&:before': {
            maxHeight: textPlacement === 'top' ? '24px' : undefined,
          },
          '&:after': {
            maxHeight: textPlacement === 'bottom' ? '24px' : undefined,
          },
        },
        !isVertical && {
          alignItems: 'center',
          '&::before, &::after': {
            borderTopWidth: border.borderWidth,
            position: 'relative',
            top: '50%',
            width: '100%',
            flex: '1 1 auto',
          },
          '&:before': {
            maxWidth: textPlacement === 'left' ? '24px' : undefined,
          },
          '&:after': {
            maxWidth: textPlacement === 'right' ? '24px' : undefined,
          },
        },
      ]}
      role="separator"
    >
      <span
        css={{
          alignSelf: 'center',
          display: 'inline-block',
          paddingBlock: theme.spacing[2],
          paddingInline: !isVertical ? theme.spacing[4] : 0,
          width: 'fit-content',
        }}
      >
        {typeof children === 'string' ? <Text variant="labelM">{children}</Text> : children}
      </span>
    </div>
  );
}
